import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import {
    ArrowRight,
    CheckCircle2,
    GraduationCap,
    Briefcase,
    ListChecks,
    ShieldCheck,
    Camera,
    Wrench,
    Zap,
    Eye,
    Star,
    Building2,
    KeyRound,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function Landing() {
    const [reviews, setReviews] = useState([]);
    // Profawz now operates on an invitation-only model — no Stripe pricing surfaces.
    const freeMode = true;
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/reviews/published");
                setReviews(data);
            } catch { /* ignore */ }
        })();
    }, []);
    return (
        <div className="min-h-screen bg-paper">
            <Navbar />

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-rule">
                <div className="absolute inset-0 grid-bg pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="label-eyebrow mb-5">Plateforme officielle de suivi d'alternance</div>
                        <h1 className="h-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95]">
                            Suivre ta <span className="text-orange">progression</span>,<br />
                            réussir <span className="relative inline-block">
                                <span className="relative z-10">ton BTS</span>
                                <span className="absolute inset-x-0 bottom-1 h-3 bg-orange/30 -z-0" />
                            </span>.
                        </h1>
                        <p className="mt-6 max-w-xl text-base sm:text-lg text-ink/70 leading-relaxed">
                            Vos alternants <strong className="text-ink">BTS Électrotechnique</strong> remplissent leur bilan en ligne. Vous, formateurs, retrouvez <strong>tous les bilans de votre établissement</strong> dans un espace privé — sans e-mail, sans paperasse.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/register" className="btn-primary text-base !px-6 !py-3" data-testid="hero-cta-register">
                                Démarrer maintenant <ArrowRight size={18} />
                            </Link>
                            <a href="#pricing" className="btn-outline text-base !px-6 !py-3" data-testid="hero-cta-pricing">
                                Comment accéder à Profawz
                            </a>
                        </div>
                        <div className="mt-10 flex items-center gap-6 text-xs font-mono text-muted2">
                            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-orange" /> RGPD & cookies sécurisés</span>
                            <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-orange" /> Référentiel officiel BTS</span>
                        </div>
                    </div>

                    <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "120ms" }}>
                        <div className="relative">
                            <div className="absolute -inset-3 border border-ink/10 -z-10" />
                            <div className="aspect-[4/5] bg-ink overflow-hidden relative">
                                <img
                                    src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80"
                                    alt="Tableau de bord — courbe de progression des compétences BTS"
                                    className="w-full h-full object-cover opacity-95"
                                />
                                {/* Progression curve overlay */}
                                <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
                                    <defs>
                                        <linearGradient id="curveFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#15A36A" stopOpacity="0.55" />
                                            <stop offset="100%" stopColor="#15A36A" stopOpacity="0" />
                                        </linearGradient>
                                    </defs>
                                    <path d="M0,260 C60,250 100,240 140,210 C180,180 220,150 260,110 C300,75 340,55 400,40 L400,300 L0,300 Z" fill="url(#curveFill)" />
                                    <path d="M0,260 C60,250 100,240 140,210 C180,180 220,150 260,110 C300,75 340,55 400,40" stroke="#22C075" strokeWidth="3" fill="none" strokeLinecap="round" />
                                    {/* Markers */}
                                    {[[40,255],[140,210],[260,110],[360,55]].map((p) => (
                                        <g key={`${p[0]}-${p[1]}`}>
                                            <circle cx={p[0]} cy={p[1]} r="6" fill="#FFFFFF" stroke="#15A36A" strokeWidth="2.5" />
                                        </g>
                                    ))}
                                </svg>
                            </div>
                            <div className="absolute -bottom-4 -left-4 bg-paper border border-ink/15 px-4 py-3 shadow-sm">
                                <div className="label-eyebrow">Référentiel intégré</div>
                                <div className="text-xs text-muted2 mt-1">BTS Électrotechnique · Référentiel officiel</div>
                            </div>
                            <div className="absolute -top-4 -right-4 bg-orange text-white px-4 py-3 border border-orange-700">
                                <div className="text-[10px] uppercase tracking-widest opacity-80">Accès sur invitation</div>
                                <div className="h-display text-3xl font-bold leading-none">Profawz</div>
                                <div className="text-[11px] opacity-80">Suivi BTS Électrotechnique</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TWO SPACES */}
            <section className="border-b border-rule py-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="label-eyebrow mb-3">Deux espaces, une plateforme</div>
                    <h2 className="h-display text-3xl sm:text-4xl font-semibold mb-12 max-w-2xl">
                        Chacun sa place, chacun son accès.
                    </h2>

                    <div className="grid md:grid-cols-2 gap-px bg-rule border border-rule">
                        {/* ALTERNANT */}
                        <div className="bg-paper p-10">
                            <GraduationCap size={32} strokeWidth={1.5} className="text-orange mb-5" />
                            <div className="label-eyebrow text-orange mb-1">Espace Alternant</div>
                            <h3 className="h-display text-2xl font-semibold mb-3">Vos étudiants</h3>
                            <p className="text-sm text-ink/70 leading-relaxed">
                                Créent un compte <strong>gratuit</strong>, remplissent leur bilan en 6 étapes après chaque période en entreprise, ajoutent leurs photos. C'est tout.
                            </p>
                            <ul className="mt-5 space-y-2 text-sm">
                                <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-orange shrink-0 mt-0.5" /> Inscription libre</li>
                                <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-orange shrink-0 mt-0.5" /> Compétences cochées dans le référentiel officiel</li>
                                <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-orange shrink-0 mt-0.5" /> Upload de photos d'activités</li>
                                <li className="flex items-start gap-2"><CheckCircle2 size={15} className="text-orange shrink-0 mt-0.5" /> Historique consultable de leurs bilans</li>
                            </ul>
                        </div>
                        {/* FORMATEUR */}
                        <div className="bg-ink text-white p-10">
                            <Briefcase size={32} strokeWidth={1.5} className="text-orange mb-5" />
                            <div className="label-eyebrow text-orange mb-1">Espace Formateur</div>
                            <h3 className="h-display text-2xl font-semibold mb-3">Vos formateurs</h3>
                            <p className="text-sm text-white/70 leading-relaxed">
                                Compte <strong>gratuit</strong> pour chaque formateur. Ils utilisent le code d'établissement fourni par le <strong>responsable de formation</strong> et accèdent à tous les bilans + au module cours/TPs.
                            </p>
                            <ul className="mt-5 space-y-2 text-sm text-white/85">
                                <li className="flex items-start gap-2"><Eye size={15} className="text-orange shrink-0 mt-0.5" /> Vision d'ensemble de tous les alternants</li>
                                <li className="flex items-start gap-2"><Eye size={15} className="text-orange shrink-0 mt-0.5" /> Logue cours/TPs avec évaluation Qualiopi</li>
                                <li className="flex items-start gap-2"><Eye size={15} className="text-orange shrink-0 mt-0.5" /> Filtres par filière, niveau, recherche</li>
                                <li className="flex items-start gap-2"><Eye size={15} className="text-orange shrink-0 mt-0.5" /> Détails compétences évaluées et photos</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="border-b border-rule py-20 bg-surface">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="label-eyebrow mb-3">Le parcours alternant</div>
                    <h2 className="h-display text-3xl sm:text-4xl font-semibold mb-12 max-w-2xl">
                        Six étapes guidées, un bilan exhaustif.
                    </h2>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule">
                        {[
                            { icon: GraduationCap, title: "Identité", body: "Nom, prénom, niveau, filière, tuteur, dates de la période d'alternance." },
                            { icon: ListChecks, title: "Compétences", body: "Cochez les compétences évaluées dans le référentiel officiel BTS." },
                            { icon: Wrench, title: "Outils", body: "Matériel et logiciels utilisés (multimètre, AutoCAD, Schneider Unity Pro…)." },
                            { icon: ListChecks, title: "Activités", body: "Décrivez précisément les activités réalisées en entreprise." },
                            { icon: ListChecks, title: "Difficultés", body: "Notez ce qui a été difficile pour mieux progresser." },
                            { icon: Camera, title: "Photos", body: "Ajoutez vos photos d'activités. C'est terminé : votre formateur voit le bilan." },
                        ].map((step, i) => (
                            <div key={step.title} className="bg-paper p-7">
                                <div className="text-xs font-mono text-orange mb-3">0{i + 1} / 06</div>
                                <step.icon size={24} className="text-ink mb-4" strokeWidth={1.5} />
                                <h3 className="h-display text-lg font-semibold">{step.title}</h3>
                                <p className="text-sm text-ink/70 leading-relaxed mt-2">{step.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ACCÈS SUR INVITATION */}
            <section id="pricing" className="py-24 border-b border-rule">
                <div className="max-w-6xl mx-auto px-6 lg:px-10">
                    <div className="text-center">
                        <div className="label-eyebrow mb-3 text-orange">Accès sur invitation</div>
                        <h2 className="h-display text-3xl sm:text-5xl font-semibold mb-3">
                            Profawz est une plateforme <span className="text-orange">sur invitation</span>
                        </h2>
                        <p className="text-ink/70 max-w-2xl mx-auto">
                            Pas de paiement en ligne, pas de carte bancaire, pas d'abonnement à gérer depuis la plateforme. Le responsable de formation contacte l'équipe Profawz pour activer son établissement, reçoit un code d'établissement, et donne ce code à ses formateurs et alternants.
                        </p>
                    </div>
                    <div className="panel p-10 mt-12 max-w-3xl mx-auto bg-surface" data-testid="landing-invitation-card">
                        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm max-w-xl mx-auto">
                            {[
                                "Aucune carte bancaire demandée",
                                "Établissement isolé · code privé",
                                "Alternants & formateurs illimités",
                                "Référentiel BTS Électrotechnique complet",
                                "Évaluation Qualiopi 4 statuts",
                                "Espace tuteur entreprise dédié",
                                "Bulletins automatiques par e-mail",
                                "Accompagnement direct par l'équipe Profawz",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-2">
                                    <CheckCircle2 size={14} className="text-orange shrink-0 mt-0.5" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="text-center mt-8">
                            <Link to="/contact" className="btn-primary !px-8 !py-3" data-testid="landing-contact-cta">
                                Demander un accès <ArrowRight size={16} />
                            </Link>
                            <p className="text-[11px] font-mono text-muted2 mt-3">L'équipe Profawz vous recontacte sous 48 h</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS — INVITE CODE FLOW */}
            <section className="py-20 border-b border-rule bg-ink text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="label-eyebrow text-orange/80 mb-3">Comment ça marche pour votre lycée ?</div>
                    <h2 className="h-display text-3xl sm:text-4xl font-semibold mb-12 max-w-2xl">
                        Trois étapes simples pour démarrer.
                    </h2>
                    <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {[
                            {
                                icon: Building2,
                                step: "01",
                                title: "Le responsable de formation s'inscrit",
                                body: "Le responsable contacte l'équipe Profawz pour activer son établissement. Il reçoit un code d'établissement unique (ex: BTS-A4K9X2) et crée son compte sur la plateforme.",
                            },
                            {
                                icon: KeyRound,
                                step: "02",
                                title: "Le code est partagé à l'équipe",
                                body: "Le responsable communique le code aux formateurs et alternants par e-mail, oral, ou affichage en classe. C'est la clé d'accès à votre espace privé.",
                            },
                            {
                                icon: GraduationCap,
                                step: "03",
                                title: "Formateurs et alternants s'inscrivent gratuitement",
                                body: "Chaque formateur et chaque alternant crée son compte gratuit avec le code. Le responsable porte l'abonnement pour tout l'établissement.",
                            },
                        ].map((s) => (
                            <div key={s.step} className="bg-ink p-7">
                                <div className="text-xs font-mono text-orange mb-3">{s.step} / 03</div>
                                <s.icon size={28} className="text-orange mb-4" strokeWidth={1.5} />
                                <h3 className="h-display text-lg font-semibold">{s.title}</h3>
                                <p className="text-sm text-white/70 leading-relaxed mt-2">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            {reviews.length > 0 && (
                <section className="py-20 border-b border-rule bg-surface">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        <div className="label-eyebrow mb-3">Avis des utilisateurs</div>
                        <h2 className="h-display text-3xl sm:text-4xl font-semibold mb-3 max-w-2xl">
                            Ils utilisent Profawz au quotidien.
                        </h2>
                        <p className="text-ink/70 max-w-xl">Retours authentiques d'alternants et de formateurs déjà inscrits sur la plateforme.</p>

                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-10">
                            {reviews.slice(0, 9).map((r) => (
                                <div key={r.id} className="panel p-5 bg-paper">
                                    <div className="flex gap-0.5 mb-3">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} size={14} className={n <= r.rating ? "text-orange" : "text-rule"} fill={n <= r.rating ? "currentColor" : "none"} />
                                        ))}
                                    </div>
                                    <p className="text-sm leading-relaxed">"{r.comment}"</p>
                                    <div className="mt-4 pt-3 border-t border-rule text-xs">
                                        <div className="font-semibold text-ink">{r.name}</div>
                                        <div className="font-mono text-muted2">
                                            {r.role_label}{r.etablissement ? ` · ${r.etablissement}` : ""}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FOOTER */}
            <Footer />
        </div>
    );
}
